import { useState } from 'react'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'

import Link from '@/components/ui/Link'
import SuggestionForm from '@/components/ui/SuggestionForm'
import Overlay from './Overlay'
import CornerGradient from '@/components/ui/CornerGradient'
import { AppCard } from './ChatApps'

import { Icon } from '@/icons/Icon'
import { useChatContext } from '@/context/ChatContext'
import { easeShort } from '@/lib/transitions'

type SuggestionStatusProps = {
  icon: React.ReactNode
  title: string
  subtitle: string
  className?: string
}

export default function Suggestion() {
  const [response, setResponse] = useState<'success' | 'error' | null>(null)

  const { collection, showSuggestion, setShowSuggestion } = useChatContext()

  const getSuggestionIcon = () => {
    switch (collection) {
      case 'review':
        return 'review'
      case 'deal':
        return 'deal'
      default:
        return 'magnifier-small'
    }
  }

  const isSuccess = response === 'success'

  const successProps = {
    icon: 'circle-check',
    title: 'Thanks for your suggestion!',
    subtitle: 'Your suggestion has been submitted. Our editors will check it out soon.',
  }

  const idleProps = {
    icon: getSuggestionIcon(),
    title: `Suggest a new ${collection ?? 'review'}`,
    subtitle: 'Tell us what to offer next. Suggest an app review and we’ll take it from here.',
  }

  return (
    <>
      <Motion>
        <m.div
          initial={{ opacity: 0, scale: 0.75, x: '-50%', y: '-50%' }}
          animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
          exit={{ opacity: 0, scale: 0.75, x: '-50%', y: '-50%' }}
          transition={easeShort}
          className="fixed left-1/2 top-1/2 z-[99] flex w-[calc(100%-20px)] max-w-content flex-col gap-8 overflow-hidden rounded-2xl bg-white p-6 shadow-button"
        >
          <CornerGradient
            corner="top-left"
            size="w-72"
            color={isSuccess ? 'bg-success-500/20' : undefined}
            className="z-0 -translate-x-32"
          />
          <CornerGradient
            corner="top-right"
            size="w-72"
            color={isSuccess ? 'bg-success-500/20' : undefined}
            className="z-0 translate-x-32"
          />

          <div className="relative mt-8 h-16 [mask-image:linear-gradient(to_right,transparent_0%,black,transparent_100%)] md:[mask-image:linear-gradient(to_right,transparent_10%,black,transparent_90%)]">
            <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-4 overflow-hidden">
              {Array.from({ length: 3 }).map((_, index) => (
                <AppCard key={index} suggestion={collection ?? 'App'} />
              ))}
            </div>
          </div>

          <AnimatePresence initial={false} mode="wait">
            {isSuccess ? (
              <SuggestionStatus
                key="success"
                icon={<Icon name={successProps.icon} className="text-success-500" />}
                title={successProps.title}
                subtitle={successProps.subtitle}
              />
            ) : (
              <SuggestionStatus
                key="idle"
                icon={<Icon name={idleProps.icon} className="text-primary-500" />}
                title={idleProps.title}
                subtitle={idleProps.subtitle}
              />
            )}
          </AnimatePresence>

          <SuggestionForm prefill={showSuggestion} response={response} setResponse={setResponse} />

          <p className="mt-5 text-center text-12 text-gray-500">
            We’ll reach out by email if your suggestion makes it to our next deal lineup.{' '}
            <Link href="/" className="underlined primary">
              Learn more
            </Link>
          </p>

          <div
            onClick={() => setShowSuggestion(null)}
            className="absolute right-0 top-0 z-50 flex h-7 w-7 cursor-pointer items-center justify-center text-gray-400 transition-colors hover:text-gray-950"
          >
            <Icon name="xmark" size={12} />
          </div>
        </m.div>
      </Motion>
      <Overlay close={() => setShowSuggestion(null)} background={true} z={30} />
    </>
  )
}

export function SuggestionStatus({
  icon,
  title,
  subtitle,
  className = 'flex flex-col items-center gap-3',
}: SuggestionStatusProps) {
  return (
    <Motion>
      <m.div
        key="suggestion-status"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        transition={easeShort}
        className={className}
      >
        {icon}
        <div className="flex flex-col gap-2">
          <span className="text-center text-14 font-medium leading-tight text-gray-950">
            {title}
          </span>
          <span className="text-center text-13 text-gray-600">{subtitle}</span>
        </div>
      </m.div>
    </Motion>
  )
}
